import type {
  ApiStreamEvent,
  ApiStreamResult,
  InvokeApiStreamRouteProps,
} from '../types';
import {
  buildRequestHeaders,
  buildRouteUrl,
  safeJsonParse,
  type NormalizeApiError,
} from './httpRequest';

interface CreateApiStreamRouteInvokerProps {
  apiBaseUrl: string;
  getIdToken: () => Promise<string | undefined>;
  normalizeApiError: NormalizeApiError;
}

// Pull complete SSE events out of an incoming UTF-8 byte stream.
//
// Per the SSE spec a line is terminated by `\r\n`, `\r`, or `\n`, and an
// event ends at the first blank line — i.e. any pair of consecutive line
// terminators. Rather than search for every blank-line byte combination
// (`\n\n`, `\r\n\r\n`, `\r\r`, `\r\n\r`, `\n\r`, …), we incrementally
// rewrite incoming bytes into a normalized buffer that uses LF only, and
// then look for the simple `\n\n` pattern there.
//
// The catch: a `\r` at the very end of a chunk could be either a CR-only
// terminator or the first byte of a CRLF pair finishing in the next chunk.
// We hold that trailing `\r` in the `pendingCR` flag and resolve it once
// the next byte (or end-of-stream) arrives.
const createEventChunkReader = (
  body: ReadableStream<Uint8Array>,
): { next: () => Promise<string | null>; cancel: () => Promise<void> } => {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let normalizedBuffer = '';
  let pendingCR = false;
  let closed = false;

  const ingest = (incoming: string): void => {
    if (incoming.length === 0) {
      return;
    }

    let toProcess = incoming;

    if (pendingCR) {
      // Either way the held `\r` is one terminator → one `\n` in the
      // normalized buffer. If the next byte is `\n`, the original `\r\n`
      // was a single CRLF terminator and we drop that `\n` so it isn't
      // double-counted.
      if (toProcess.startsWith('\n')) {
        toProcess = toProcess.slice(1);
      }
      normalizedBuffer += '\n';
      pendingCR = false;
    }

    if (toProcess.endsWith('\r')) {
      // Defer the trailing CR until the next byte arrives.
      pendingCR = true;
      toProcess = toProcess.slice(0, -1);
    }

    // Normalize the inner CR/CRLF sequences. CRLF first (so a `\r\n` pair
    // collapses to one `\n` instead of two), then any remaining bare `\r`.
    normalizedBuffer += toProcess.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  };

  const flushPendingCR = (): void => {
    if (pendingCR) {
      // Stream closed with a bare `\r` — treat as a CR-only terminator.
      normalizedBuffer += '\n';
      pendingCR = false;
    }
  };

  const pumpUntilEvent = async (): Promise<string | null> => {
    while (true) {
      const terminatorIndex = normalizedBuffer.indexOf('\n\n');
      if (terminatorIndex !== -1) {
        const rawEvent = normalizedBuffer.slice(0, terminatorIndex);
        normalizedBuffer = normalizedBuffer.slice(terminatorIndex + 2);
        return rawEvent;
      }

      if (closed) {
        return null;
      }

      const { value, done } = await reader.read();
      if (done) {
        closed = true;
        // Flush any remaining bytes from the decoder (a multi-byte char split
        // across the final chunk boundary would otherwise be lost).
        ingest(decoder.decode());
        flushPendingCR();
        // The server may close without a trailing blank line. Surface whatever
        // is left as a final event so callers don't lose the last payload.
        const trailing = normalizedBuffer;
        normalizedBuffer = '';
        return trailing.length > 0 ? trailing : null;
      }

      if (value) {
        // `stream: true` keeps multi-byte characters split across chunk
        // boundaries from being mis-decoded as replacement characters.
        ingest(decoder.decode(value, { stream: true }));
      }
    }
  };

  return {
    next: pumpUntilEvent,
    cancel: async () => {
      closed = true;
      try {
        await reader.cancel();
      } catch {
        // Best-effort: the underlying source may already have errored.
      }
    },
  };
};

const parseSseEvent = (rawEvent: string): ApiStreamEvent | null => {
  let type: string | undefined;
  let id: string | undefined;
  // SSE `data:` lines accumulate and are joined by `\n` to form the payload.
  // A raw event with zero `data:` lines (e.g. a comment-only block) is a
  // no-op and we drop it.
  const dataLines: string[] = [];

  // The chunk reader already normalized every legal SSE line terminator
  // (`\r\n`, `\r`, `\n`) to `\n`, so a simple LF split is sufficient here.
  for (const line of rawEvent.split('\n')) {
    if (line.length === 0) {
      // Embedded blank lines inside an event are impossible — the event
      // terminator regex would have ended the event there. A leading or
      // trailing empty entry from `split` (e.g. when the raw event starts
      // with a line break) is harmless; skip it.
      continue;
    }

    if (line.startsWith(':')) {
      // SSE comment line. Ignore.
      continue;
    }

    const colonIndex = line.indexOf(':');
    const fieldName = colonIndex === -1 ? line : line.slice(0, colonIndex);
    // Per the spec, a single leading space after the colon is part of the
    // delimiter and stripped. Additional whitespace is preserved.
    let value = colonIndex === -1 ? '' : line.slice(colonIndex + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    if (fieldName === 'event') {
      type = value;
    } else if (fieldName === 'id') {
      id = value;
    } else if (fieldName === 'data') {
      dataLines.push(value);
    }
    // `retry` and unknown field names are ignored.
  }

  if (dataLines.length === 0) {
    return null;
  }

  const dataPayload = dataLines.join('\n');

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataPayload);
  } catch {
    // The api-longrun-node SSE writer JSON-encodes every payload, so a parse
    // failure is a contract violation rather than an expected branch. Surface
    // the raw string so the consumer can decide how to handle it.
    parsed = dataPayload;
  }

  return {
    type,
    id,
    data: parsed,
  };
};

export const createApiStreamRouteInvoker =
  ({
    apiBaseUrl,
    getIdToken,
    normalizeApiError,
  }: CreateApiStreamRouteInvokerProps) =>
  async <TEvent extends ApiStreamEvent = ApiStreamEvent>(
    props: InvokeApiStreamRouteProps,
  ): Promise<ApiStreamResult<TEvent>> => {
    const {
      method,
      path,
      params,
      query,
      headers: customHeaders,
      body,
      requestData,
      signal,
    } = props;
    const headers = await buildRequestHeaders({
      getIdToken,
      customHeaders,
      defaultHeaders: { accept: 'text/event-stream' },
    });
    const requestBody = 'body' in props ? body : requestData;

    // Aborting `signal` causes fetch to reject (before `await` resolves) or
    // closes the body stream (after); the iterator's `read()` then completes
    // with `done: true` or rejects, so callers don't need a second listener.
    const response = await fetch(
      buildRouteUrl(apiBaseUrl, path, params, query),
      {
        method,
        credentials: 'include',
        headers,
        body:
          method === 'GET'
            ? undefined
            : JSON.stringify(requestBody == null ? {} : requestBody),
        signal,
      },
    );

    if (!response.ok) {
      const payload = await safeJsonParse(response);

      throw normalizeApiError({
        payload,
        statusCode: response.status,
        fallbackMessage: `Request failed with status ${response.status}`,
      });
    }

    if (!response.body) {
      // A 2xx response with no readable body is treated as an empty stream
      // rather than an error: some runtimes (e.g. fake responses in tests)
      // return null bodies, and an empty stream is a graceful no-op.
      return { stream: emptyAsyncIterable<TEvent>() };
    }

    const chunkReader = createEventChunkReader(response.body);

    const stream: AsyncIterable<TEvent> = {
      [Symbol.asyncIterator]() {
        return {
          async next(): Promise<IteratorResult<TEvent>> {
            while (true) {
              const rawEvent = await chunkReader.next();
              if (rawEvent === null) {
                return { value: undefined, done: true };
              }

              const event = parseSseEvent(rawEvent);
              if (event === null) {
                // Comment-only or data-less events are spec-legal but carry
                // no payload — keep pumping until we have a real event or
                // the stream ends.
                continue;
              }

              // The runtime shape is `ApiStreamEvent` (with `data: unknown`).
              // We trust the caller's `TEvent` declaration to describe the
              // route's chunk schema; this is the same type-system boundary
              // we cross at any other JSON-over-network seam.
              return { value: event as TEvent, done: false };
            }
          },
          async return(): Promise<IteratorResult<TEvent>> {
            // Called when a `for await` loop is broken out of early. Releasing
            // the reader lets fetch tear down the underlying connection.
            await chunkReader.cancel();
            return { value: undefined, done: true };
          },
        };
      },
    };

    return { stream };
  };

const emptyAsyncIterable = <TEvent>(): AsyncIterable<TEvent> => ({
  [Symbol.asyncIterator]() {
    return {
      async next(): Promise<IteratorResult<TEvent>> {
        return { value: undefined, done: true };
      },
    };
  },
});
